// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — ProcessMapLevel3 (Prompt 146: UX Final)
// TopBar 48px + SideCard 200px + FlowViewer card + modal sections
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import { useI18n } from '../../../../../i18n';
import { formatDate } from '../../../../../lib/design';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { fetchFicha, request } from '../../../../shared/services/api';
import type { FichaPanelData } from '../../../../shared/services/api';
import { useProcessNavigation } from '../hooks/useProcessNavigation';
import FlowViewer from '../../flow/viewer/FlowViewer';
import { DocumentPanel } from '../../docs/components/DocumentPanel';
import { TocSidebar } from '../../docs/viewer/TocSidebar';
import { DocTypeBadge, StatusBadge } from '../../../../../lib/ui';
import { extractToc } from '../../docs/viewer/renderContent';
import type { TocItem } from '../../docs/viewer/renderContent';
import { ViewerStyles } from '../../docs/viewer/ViewerStyles';
import type { DocumentoResumen } from '../../../../../types';
import type { ProcessSection } from '../types/process-map.types';
import type { Proceso } from '../../../../../types';
import { TIPOLOGIA_LABEL } from '../../../../../types';
import {
  IconDescripcion,
  IconResponsables,
  IconInvolucrados,
  IconDocumentacion,
  IconRequisitos,
  IconReportes,
  IconHerramientas,
  IconIndicadores,
  IconFormacion,
  IconDownload,
} from './ProcessMapIcons';

// ─── Colors (spec 146) ──────────────────────────────────────────────────────

const C = {
  bg: '#F2F4F8',
  navy: '#1F3864',
  navy2: '#2F5496',
  textMut: '#A0AABF',
  border: '#E0E5EF',
  borderSoft: '#E4E8F0',
  borderCard: '#EEF1F7',
  hoverBg: '#EEF2FB',
  hoverBorder: '#C8D4EE',
  tagBg: '#EEF2FB',
  tagBorder: '#C8D4EE',
  pillBg: '#F5F6FA',
  pillBorder: '#E4E8F0',
  sideCardShadow: '0 2px 12px rgba(31,56,100,0.08)',
  flowCardShadow: '0 2px 16px rgba(31,56,100,0.09)',
  overlayBg: 'rgba(10,18,45,0.32)',
  modalShadow: '0 16px 48px rgba(15,25,60,0.22)',
  cardBg: '#FAFBFD',
  iconWrap: '#EEF2FB',
};

// ─── Section definitions ─────────────────────────────────────────────────────

interface SectionDef {
  key: ProcessSection;
  label: string;
  sub: string;
  Icon: React.FC<{ size?: number; color?: string }>;
}

// Sections initialized in Spanish — overridden at runtime via getSections()
// (kept as static reference only, dynamic version used at render time)

function getSections(t: any): SectionDef[] {
  return [
    {
      key: 'descripcion',
      label: t.processMap.descripcion,
      sub: t.processMap.objetoYAlcance,
      Icon: IconDescripcion,
    },
    {
      key: 'responsables',
      label: t.processMap.responsables,
      sub: t.processMap.direccionYRepresentante,
      Icon: IconResponsables,
    },
    {
      key: 'involucrados',
      label: t.processMap.involucrados,
      sub: t.processMap.rolesYParticipantes,
      Icon: IconInvolucrados,
    },
    { key: 'documentacion', label: t.processMap.documentacion, sub: '', Icon: IconDocumentacion },
    {
      key: 'requisitos',
      label: t.processMap.requisitosLabel,
      sub: t.processMap.normasAplicables,
      Icon: IconRequisitos,
    },
    {
      key: 'reportes',
      label: t.processMap.reportesLabel,
      sub: t.processMap.trimestralCMI,
      Icon: IconReportes,
    },
    {
      key: 'herramientas',
      label: t.processMap.herramientasLabel,
      sub: 'V\u00b7IANet M9 M10',
      Icon: IconHerramientas,
    },
    { key: 'indicadores', label: t.processMap.indicadoresLabel, sub: '', Icon: IconIndicadores },
    {
      key: 'formacion',
      label: t.processMap.formacion,
      sub: t.processMap.onboardingAnual,
      Icon: IconFormacion,
    },
  ];
}

// ─── Tipologia short labels ──────────────────────────────────────────────────

// Tipologia short labels — overridden at runtime via t.processMap.*

// ─── Modal keyframe ──────────────────────────────────────────────────────────

const MODAL_ANIM = `
@keyframes mIn {
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}
`;

// ─── Main Component ──────────────────────────────────────────────────────────

export function ProcessMapLevel3() {
  const { t, lang } = useI18n();
  const { processId } = useParams<{ processId: string }>();
  const navigate = useNavigate();
  const { goToLevel1, goToLevel2 } = useProcessNavigation();

  const [proceso, setProceso] = useState<Proceso | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<ProcessSection | null>(null);

  // Document embed state
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeDoc, setActiveDoc] = useState<string | null>(searchParams.get('doc') ?? null);
  // Documentos visitados — se acumulan para mantener instancias cacheadas
  const [visitedDocs, setVisitedDocs] = useState<string[]>(() => {
    const init = searchParams.get('doc');
    return init ? [init] : [];
  });
  const openDoc = useCallback(
    (codigo: string) => {
      setActiveDoc(codigo);
      setSearchParams({ doc: codigo }, { replace: true });
      setVisitedDocs((prev) => (prev.includes(codigo) ? prev : [...prev, codigo]));
    },
    [setSearchParams],
  );
  const closeDoc = useCallback(() => {
    setActiveDoc(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // TOC extraction for embedded document
  const [docToc, setDocToc] = useState<TocItem[]>([]);
  const [docZoom, setDocZoom] = useState(85);
  const [docNombre, setDocNombre] = useState<string | null>(null);
  const [docEstado, setDocEstado] = useState<string | null>(null);
  const exportPdfRef = useRef<(() => Promise<void>) | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const handleExportPdf = useCallback(async () => {
    if (!exportPdfRef.current) return;
    setExportingPdf(true);
    try {
      await exportPdfRef.current();
    } finally {
      setExportingPdf(false);
    }
  }, []);

  // Cached fichaPanel — loaded once with the process
  const [fichaPanel, setFichaPanel] = useState<FichaPanelData | null>(null);
  const [fichaLoading, setFichaLoading] = useState(true);

  useEffect(() => {
    if (!activeDoc) {
      setDocToc([]);
      setDocNombre(null);
      setDocEstado(null);
      return;
    }
    type DocDTO = {
      nombre?: string;
      titulo?: string;
      estado?: string;
      versionVigente?: { contenidoJson?: unknown; estado?: string };
    };
    request<{ data?: DocDTO } & DocDTO>(`/documentos/${encodeURIComponent(activeDoc)}`)
      .then((json) => {
        const d: DocDTO = json.data ?? json;
        const cj = d.versionVigente?.contenidoJson ?? null;
        setDocToc(cj ? extractToc(cj) : []);
        setDocNombre(d.nombre ?? d.titulo ?? null);
        // 0661.7: REGLA DE PRODUCTO — el nivel 3 (mapa de procesos) es SOLO
        // un visor de la version VIGENTE PUBLICADA. NO se gestiona workflow
        // aqui (eso vive en /process?tab=caracterizacion).
        // Por tanto el badge debe reflejar el estado de versionVigente
        // (PUBLICADO/APROBADO segun lifecycle), nunca el del documento padre
        // que cambia con cada nueva edicion en EN_REVISION.
        setDocEstado(d.versionVigente?.estado ?? d.estado ?? null);
      })
      .catch(() => {
        setDocToc([]);
        setDocNombre(null);
        setDocEstado(null);
      });
  }, [activeDoc]);

  useEffect(() => {
    if (!processId) return;
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const json = await request<{ data?: Proceso[]; datos?: Proceso[] }>('/procesos');
        const all: Proceso[] = json.data ?? json.datos ?? [];
        // Aceptar tanto UUID como código V·IAnet (ej: "PO.74" desde V·IA Chat)
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          processId ?? '',
        );
        const found = all.find((p) => (isUuid ? p.id === processId : p.codigo === processId));
        if (!cancelled) {
          if (found) {
            setProceso(found);
            fetchFicha(found.id)
              .then((res) => {
                if (!cancelled) {
                  setFichaPanel(res.data);
                  setFichaLoading(false);
                }
              })
              .catch(() => {
                if (!cancelled) setFichaLoading(false);
              });
          } else {
            setError(t.processMap.procesoNoEncontrado);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [processId, lang]);

  const localSections = getSections(t);

  // Dynamic sub for documentacion & indicadores
  const getSub = (key: ProcessSection): string => {
    if (!proceso) return '';
    if (key === 'documentacion') {
      const n = proceso._count?.documentos ?? proceso.documentos?.length ?? 0;
      return `${n} documento${n !== 1 ? 's' : ''}`;
    }
    if (key === 'indicadores') return t.processMap.kpisDefinidos(0);
    return localSections.find((s) => s.key === key)?.sub ?? '';
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: C.bg,
          fontFamily: 'Montserrat, sans-serif',
        }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner text={t.processMap.cargandoProceso} />
        </div>
      </div>
    );
  }

  if (error || !proceso) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: C.bg,
          fontFamily: 'Montserrat, sans-serif',
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              color: '#DC2626',
              fontSize: 13,
              backgroundColor: '#FEF2F2',
              padding: '12px 20px',
              borderRadius: 10,
              border: '1px solid #FECACA',
            }}
          >
            {error ?? t.processMap.procesoNoEncontrado}
          </div>
          <button
            onClick={goToLevel1}
            style={{
              fontSize: 13,
              color: C.navy2,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t.processMap.volverAlMapa}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: C.bg,
        fontFamily: 'Montserrat, sans-serif',
      }}
    >
      <TopBar
        proceso={proceso}
        onGoBack={() => goToLevel2(proceso.grupoNumero, proceso.tipologia)}
      />

      {/* Main: side-card + flow-card */}
      <div style={{ flex: 1, display: 'flex', padding: 14, gap: 12, minHeight: 0 }}>
        <SideCard getSub={getSub} onSectionClick={(key) => setActiveSection(key)} />

        {/* Flow / Document card */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            backgroundColor: '#fff',
            borderRadius: 12,
            borderTop: `2.5px solid ${C.navy}`,
            boxShadow: C.flowCardShadow,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <ViewerStyles />
          {/* FlowViewer — capa absoluta, oculta cuando hay doc activo */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: activeDoc ? 'none' : 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <FlowViewer
              procesoId={proceso.id}
              procesoCodigo={proceso.codigo}
              procesoNombre={proceso.nombre}
              hideBar
              onNavigateDocument={openDoc}
              onNavigateProcess={(codigo) => navigate(`/process-map/process/${codigo}`)}
            />
          </div>
          {/* Visor de documento — instancias cacheadas por documento visitado */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: activeDoc ? 'flex' : 'none',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <DocCardBar
              docCodigo={activeDoc ?? ''}
              docNombre={docNombre}
              docEstado={docEstado}
              onClose={closeDoc}
              onExportPdf={handleExportPdf}
            />
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
              <TocSidebar
                toc={docToc}
                activeTocId=""
                zoom={docZoom}
                lang="es"
                sidebarOpen
                onScrollTo={(id) =>
                  document
                    .getElementById(id)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                onZoomChange={setDocZoom}
                onLangChange={() => {}}
                onExportPdf={handleExportPdf}
                exportingPdf={exportingPdf}
              />
              <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {visitedDocs.map((codigo) => (
                  <div
                    key={codigo}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: activeDoc === codigo ? 'flex' : 'none',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    <DocumentPanel
                      documento={{ codigo } as DocumentoResumen}
                      readOnly
                      viewerMode
                      useContentPages
                      externalHeader
                      externalZoom={docZoom}
                      onZoomChange={setDocZoom}
                      onExportRef={codigo === activeDoc ? exportPdfRef : undefined}
                    />
                  </div>
                ))}
              </div>
            </div>
            {/* PDF export overlay */}
          </div>
        </div>
      </div>

      {/* Modal */}
      {activeSection && (
        <SectionModal
          section={activeSection}
          proceso={proceso}
          onClose={() => setActiveSection(null)}
          fichaPanel={fichaPanel}
          fichaLoading={fichaLoading}
          onOpenDoc={openDoc}
          activeDoc={activeDoc}
        />
      )}
    </div>
  );
}

// ─── TopBar (48px) ───────────────────────────────────────────────────────────

function TopBar({ proceso, onGoBack }: { proceso: Proceso; onGoBack: () => void }) {
  const { t } = useI18n();
  const tipoShortI18n: Record<string, string> = {
    PE: t.processMap.estrategico,
    PO: t.processMap.operativo,
    PS: t.processMap.soporteCorto,
  };
  return (
    <div
      style={{
        height: 48,
        minHeight: 48,
        flexShrink: 0,
        backgroundColor: '#fff',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: 0,
      }}
    >
      {/* Back button */}
      <button
        onClick={onGoBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 12,
          fontWeight: 600,
          color: C.navy,
          padding: '6px 10px',
          borderRadius: 8,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#F0F2F7';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <svg
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="none"
          stroke={C.navy}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ borderRadius: 18 }}
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        {proceso.grupoNombre}
      </button>

      {/* Separator */}
      <div style={{ width: 1, height: 24, backgroundColor: C.borderSoft, margin: '0 12px' }} />

      {/* Codigo + nombre */}
      <span style={{ fontFamily: 'monospace', fontSize: 10, color: C.textMut, marginRight: 6 }}>
        {proceso.codigo}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: C.navy,
          marginRight: 10,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 300,
        }}
      >
        {proceso.nombre}
      </span>

      {/* Tag tipologia */}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 9,
          fontWeight: 700,
          color: C.navy2,
          backgroundColor: C.tagBg,
          border: `1px solid ${C.tagBorder}`,
          borderRadius: 4,
          padding: '2px 8px',
        }}
      >
        <svg width={10} height={10} viewBox="0 0 24 24" fill={C.navy2} stroke="none">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
        </svg>
        {tipoShortI18n[proceso.tipologia] ?? proceso.tipologia}
      </span>

      <div style={{ flex: 1 }} />

      {/* Badge Vigente */}
      {proceso.fechaVigencia && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 10,
            fontWeight: 600,
            color: '#15803D',
            backgroundColor: '#DCFCE7',
            borderRadius: 12,
            padding: '3px 10px',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#22C55E',
              boxShadow: '0 0 6px rgba(34,197,94,0.5)',
            }}
          />
          {t.processMap.vigenteBadge}
        </span>
      )}

      {/* Sep */}
      {proceso.fechaVigencia && (
        <div style={{ width: 1, height: 20, backgroundColor: C.borderSoft, margin: '0 10px' }} />
      )}

      {/* Pills: Desde · Act. · Ed. */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {proceso.fechaVigencia && (
          <Pill label={t.processMap.desdePill} value={formatDate(proceso.fechaVigencia)} />
        )}
        {proceso.fechaUltimaActualizacion && (
          <Pill label={t.processMap.actPill} value={formatDate(proceso.fechaUltimaActualizacion)} />
        )}
      </div>

      {/* Sep + PDF button */}
      <div style={{ width: 1, height: 20, backgroundColor: C.borderSoft, margin: '0 10px' }} />
      <button
        onClick={() => {}}
        title={t.processMap.exportarPdf}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          padding: '5px 12px',
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          background: '#fff',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 11,
          fontWeight: 500,
          color: C.navy,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#F5F6FA';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#fff';
        }}
      >
        <IconDownload size={13} color={C.navy} />
        PDF
      </button>
    </div>
  );
}

function Pill({ label, value }: { label: string; value: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: C.pillBg,
        border: `1px solid ${C.pillBorder}`,
        borderRadius: 5,
        padding: '2px 8px',
        lineHeight: 1.2,
      }}
    >
      <span style={{ fontSize: 8, color: C.textMut, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color: C.navy }}>{value}</span>
    </span>
  );
}

// ─── SideCard (200px) ────────────────────────────────────────────────────────

function SideCard({
  getSub,
  onSectionClick,
}: {
  getSub: (key: ProcessSection) => string;
  onSectionClick: (key: ProcessSection) => void;
}) {
  const { t } = useI18n();
  const localSections = getSections(t);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [spacing, setSpacing] = useState({ pad: 8, gap: 6 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const calc = () => {
      const h = el.clientHeight;
      setSpacing({ pad: Math.round(h * 0.05), gap: Math.round(h * 0.02) });
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      style={{
        width: 240,
        minWidth: 240,
        backgroundColor: '#fff',
        borderRadius: 12,
        borderTop: `2.5px solid ${C.navy}`,
        boxShadow: C.sideCardShadow,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 105,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '10px 12px 8px',
          borderBottom: `1px solid #F0F2F7`,
          fontSize: 8,
          fontWeight: 700,
          color: C.textMut,
          textTransform: 'uppercase',
          letterSpacing: 1.2,
        }}
      >
        {t.processMap.seccionesDelProceso}
      </div>

      {/* Cards wrap — flex col, spacing proporcional */}
      <div
        ref={wrapRef}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: `${spacing.pad}px 8px`,
          gap: spacing.gap,
          minHeight: 0,
        }}
      >
        {localSections.map(({ key, label, Icon }) => (
          <SecCard
            key={key}
            label={label}
            sub={getSub(key)}
            Icon={Icon}
            onClick={() => onSectionClick(key)}
          />
        ))}
      </div>
    </div>
  );
}

function SecCard({
  label,
  sub,
  Icon,
  onClick,
}: {
  label: string;
  sub: string;
  Icon: React.FC<{ size?: number; color?: string }>;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 12px',
        backgroundColor: hover ? C.hoverBg : C.cardBg,
        borderRadius: 8,
        border: `0.5px solid ${hover ? C.hoverBorder : C.borderCard}`,
        borderLeft: `2.5px solid ${hover ? C.navy2 : C.navy}`,
        cursor: 'pointer',
        transition: 'all 0.12s',
        minHeight: 0,
      }}
    >
      {/* Icon wrap */}
      <div
        style={{
          width: 36,
          height: 36,
          minWidth: 36,
          borderRadius: 8,
          backgroundColor: C.iconWrap,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={21} color={C.navy} />
      </div>
      {/* Info */}
      <div style={{ overflow: 'hidden' }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: C.navy,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 12,
              color: C.textMut,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SectionModal (sin blur, sin X) ──────────────────────────────────────────

function SectionModal({
  section,
  proceso,
  onClose,
  fichaPanel,
  fichaLoading,
  onOpenDoc,
  activeDoc,
}: {
  section: ProcessSection;
  proceso: Proceso;
  onClose: () => void;
  fichaPanel: FichaPanelData | null;
  fichaLoading: boolean;
  onOpenDoc?: (codigo: string) => void;
  activeDoc?: string | null;
}) {
  const { t } = useI18n();
  const localSections = getSections(t);
  const sectionDef = localSections.find((s) => s.key === section);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: MODAL_ANIM }} />
      {/* Overlay — sin backdrop-filter, sin blur */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: C.overlayBg,
          zIndex: 100,
        }}
      />
      {/* Modal card */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(560px, 90vw)',
          maxHeight: '80vh',
          backgroundColor: '#fff',
          borderRadius: 16,
          borderTop: `3px solid ${C.navy}`,
          boxShadow: C.modalShadow,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 101,
          animation: 'mIn 0.14s ease-out',
        }}
      >
        {/* Header — sin boton X */}
        <div
          style={{
            height: 48,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            borderBottom: `1px solid #F0F2F7`,
            gap: 10,
          }}
        >
          {sectionDef && (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: C.iconWrap,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <sectionDef.Icon size={20} color={C.navy} />
            </div>
          )}
          <span style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>
            {sectionDef?.label ?? section}
          </span>
        </div>
        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          <SectionContent
            section={section}
            proceso={proceso}
            fichaPanel={fichaPanel}
            fichaLoading={fichaLoading}
            onOpenDoc={onOpenDoc}
            onClose={onClose}
            activeDoc={activeDoc}
          />
        </div>
      </div>
    </>
  );
}

// ─── DocCardBar (mini-bar when document is embedded) ─────────────────────────

function DocCardBar({
  docCodigo,
  docNombre,
  docEstado,
  onClose,
  onExportPdf,
}: {
  docCodigo: string;
  docNombre?: string | null;
  docEstado?: string | null;
  onClose: () => void;
  onExportPdf?: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      style={{
        height: 40,
        minHeight: 40,
        display: 'flex',
        alignItems: 'center',
        padding: '0 14px',
        borderBottom: '1px solid #EEF1F7',
        backgroundColor: '#FAFBFD',
        gap: 10,
        flexShrink: 0,
      }}
    >
      <button
        onClick={onClose}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 11,
          fontWeight: 500,
          color: '#5A6782',
          padding: '4px 8px',
          borderRadius: 6,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#F0F3F8';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <svg
          width={10}
          height={10}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#5A6782"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        {t.processMap.volverAlFlujograma}
      </button>
      <div style={{ width: 1, height: 20, backgroundColor: '#E4E8F0' }} />
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#A0AABF' }}>
        {docCodigo}
      </span>
      {docNombre && (
        <span
          style={{
            fontSize: 13,
            color: '#374151',
            fontWeight: 500,
            maxWidth: 260,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {docNombre}
        </span>
      )}
      {docEstado && <StatusBadge status={docEstado as any} small />}
      <div style={{ flex: 1 }} />
      {onExportPdf && (
        <button
          onClick={onExportPdf}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            borderRadius: 4,
            color: '#5A6782',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F0F3F8';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Exportar PDF"
        >
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ─── SectionContent ──────────────────────────────────────────────────────────

function SectionContent({
  section,
  proceso,
  fichaPanel,
  fichaLoading,
  onOpenDoc,
  onClose: _onClose,
  activeDoc,
}: {
  section: ProcessSection;
  proceso: Proceso;
  fichaPanel: FichaPanelData | null;
  fichaLoading: boolean;
  onOpenDoc?: (codigo: string) => void;
  onClose?: () => void;
  activeDoc?: string | null;
}) {
  const { t } = useI18n();
  const emptyMsg = (text: string) => (
    <p style={{ fontSize: 12, color: C.textMut, fontStyle: 'italic' }}>{text}</p>
  );
  const chip = (text: string, i: number) => (
    <span
      key={i}
      style={{
        display: 'inline-flex',
        padding: '4px 10px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        color: C.navy,
        backgroundColor: C.tagBg,
        border: `1px solid ${C.tagBorder}`,
      }}
    >
      {text}
    </span>
  );
  const item = (icon: string, text: string, i: number) => (
    <div
      key={i}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 8,
        backgroundColor: i % 2 === 0 ? 'transparent' : '#FAFBFD',
      }}
    >
      <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>{text}</span>
    </div>
  );

  if (fichaLoading && !['descripcion', 'responsables'].includes(section)) {
    return (
      <div style={{ padding: 16, textAlign: 'center' }}>
        <LoadingSpinner text={t.common.cargando} size="sm" />
      </div>
    );
  }

  switch (section) {
    case 'descripcion':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>
            {proceso.descripcion ?? t.processMap.sinDescripcion}
          </p>
          {fichaPanel?.alcance && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  color: C.textMut,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 4,
                }}
              >
                {t.process.alcance}
              </div>
              <p style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>{fichaPanel.alcance}</p>
            </div>
          )}
          {proceso.fase && <InfoRow label={t.processSheet.fields.phase} value={proceso.fase} />}
          <InfoRow
            label={t.processMap.tipologiaLabel}
            value={TIPOLOGIA_LABEL[proceso.tipologia] ?? proceso.tipologia}
          />
          <InfoRow label={t.processMap.grupo} value={proceso.grupoNombre} />
        </div>
      );
    case 'responsables':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {proceso.representante ? (
            <div style={{ padding: '12px 14px', borderRadius: 10, backgroundColor: C.hoverBg }}>
              <div
                style={{
                  fontSize: 10,
                  color: C.textMut,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {t.process.representanteProceso}
              </div>
              <div style={{ fontSize: 13, color: C.navy, fontWeight: 600, marginTop: 4 }}>
                {proceso.representante.persona
                  ? `${proceso.representante.persona.nombre} ${proceso.representante.persona.apellidos}`
                  : proceso.representante.puesto.titulo}
              </div>
            </div>
          ) : (
            emptyMsg(t.processMap.sinResponsable)
          )}
          {proceso.direccionResponsable && (
            <div style={{ padding: '12px 14px', borderRadius: 10, backgroundColor: C.hoverBg }}>
              <div
                style={{
                  fontSize: 10,
                  color: C.textMut,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {t.process.direccionResponsable}
              </div>
              <div style={{ fontSize: 13, color: C.navy, fontWeight: 600, marginTop: 4 }}>
                {proceso.direccionResponsable.puesto.titulo}
              </div>
            </div>
          )}
        </div>
      );
    case 'involucrados':
      return fichaPanel?.involucrados.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {fichaPanel.involucrados.map((inv, i) => chip(inv, i))}
        </div>
      ) : (
        emptyMsg(t.processMap.sinInvolucrados)
      );
    case 'documentacion':
      if (fichaLoading)
        return (
          <div style={{ padding: 16, textAlign: 'center' }}>
            <LoadingSpinner text={t.common.cargando} size="sm" />
          </div>
        );
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {fichaPanel?.documentos && fichaPanel.documentos.length > 0
            ? fichaPanel.documentos.map((doc) => {
                const isActive = activeDoc === doc.codigo;
                return (
                  <div
                    key={doc.id}
                    onClick={() => {
                      onOpenDoc?.(doc.codigo);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: isActive ? '6px 12px 6px 9px' : '6px 12px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      backgroundColor: isActive ? C.hoverBg : 'transparent',
                      borderLeft: isActive ? `3px solid ${C.navy}` : '3px solid transparent',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = C.hoverBg;
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {doc.tipoDocumental && <DocTypeBadge type={doc.tipoDocumental as any} />}
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 10.5,
                        fontWeight: 500,
                        color: '#98A2B3',
                        minWidth: 72,
                        flexShrink: 0,
                      }}
                    >
                      {doc.codigo}
                    </span>
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 400,
                        color: '#101828',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {doc.titulo}
                    </span>
                  </div>
                );
              })
            : emptyMsg(t.processMap.sinDocumentosVinculados)}
        </div>
      );
    case 'requisitos':
      return fichaPanel?.requisitos.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fichaPanel.requisitos.map((r, i) => item('✅', r, i))}
        </div>
      ) : (
        emptyMsg(t.processMap.sinRequisitos)
      );
    case 'reportes':
      return fichaPanel?.reportes.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fichaPanel.reportes.map((r, i) => item('📄', r, i))}
        </div>
      ) : (
        emptyMsg(t.processMap.sinReportes2)
      );
    case 'herramientas':
      return fichaPanel?.herramientas.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fichaPanel.herramientas.map((h, i) => item('🔧', h, i))}
        </div>
      ) : (
        emptyMsg(t.processMap.sinHerramientasDefinidas)
      );
    case 'indicadores':
      return fichaPanel?.indicadores.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {fichaPanel.indicadores.map((ind) => (
            <div
              key={ind.id}
              style={{ padding: '12px 14px', borderRadius: 10, backgroundColor: C.hoverBg }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{ind.nombre}</div>
              {ind.descripcion && (
                <div style={{ fontSize: 12, color: '#555', marginTop: 4, lineHeight: 1.5 }}>
                  {ind.descripcion}
                </div>
              )}
              <div style={{ fontSize: 11, color: C.textMut, marginTop: 4 }}>
                {ind.unidad}
                {ind.valorObjetivo != null ? ` · Meta: ${ind.valorObjetivo}` : ''}
              </div>
            </div>
          ))}
        </div>
      ) : (
        emptyMsg(t.processMap.sinIndicadoresDefinidos)
      );
    case 'formacion':
      return (
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 10,
            backgroundColor: '#F0F4FF',
            border: '1px solid #D6E4F0',
          }}
        >
          <p style={{ fontSize: 13, color: C.navy, lineHeight: 1.6, margin: 0 }}>
            {t.processMap.formacionEnDesarrollo}
          </p>
        </div>
      );
    default:
      return emptyMsg(t.processMap.seccionEnDesarrollo);
  }
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{ fontSize: 10, color: C.textMut, textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: C.navy, marginTop: 2 }}>{value}</div>
    </div>
  );
}
